import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { initialValues, validationSchema } from './constant';
import { updateRatingsService } from '../../../../store/apis/performanceService';
import { getPerformanceWeightage } from '../../../Performance/common';
import { toFixedTwoDecimal } from '../../../../utils/number.util';

function EditRatingsModal({
  isOpen,
  toggle,
  selectedEmployees,
  selectedKey,
  weightagePercentage,
  reviewPrd,
  refetchAllData,
}) {
  const { id: sessionId } = useParams();
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const { mutate: updateRatings, isPending } = useMutation({
    mutationFn: updateRatingsService,
    onSuccess: () => {
      toast.success('Ratings updated successfully!', {
        className: 'toast-success',
      });
      toggle();
      reset();
      refetchAllData();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to update ratings', {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (selectedKey && isOpen) {
      const [pcm, performance] = selectedKey.split(':');
      setValue('performance', Number(performance));
      setValue('pcm', Number(pcm));
    }
  }, [setValue, isOpen, selectedKey]);

  const onSubmit = data => {
    const performanceWeightage =
      getPerformanceWeightage(weightagePercentage)?.performanceWeightage;
    const pcmWeightage =
      getPerformanceWeightage(weightagePercentage)?.pcmWeightage;
    const empCodes = Array.isArray(selectedEmployees)
      ? selectedEmployees.map(emp => emp.EmpCd).join(',')
      : '';

    const overallManagerRating =
      (performanceWeightage / 100) * parseFloat(data?.performance) +
      (pcmWeightage / 100) * parseFloat(data?.pcm);

    const payload = {
      sessionId,
      overallPerformanceRating: toFixedTwoDecimal(data?.performance),
      overallPCMRating: toFixedTwoDecimal(data?.pcm),
      overallRatingManager: toFixedTwoDecimal(overallManagerRating),
      employeesCodes: empCodes,
      reviewPrd,
    };
    updateRatings(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="custom-modal-size"
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={toggle}>
        <span className="fs-5">Update Section Ratings</span>
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <div className="mb-3">
            <Label>
              PCM
              <span className="text-danger"> *</span>
            </Label>
            <Controller
              name="pcm"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className="external-filter-input form-control"
                  type="text"
                  placeholder="Enter"
                  title="Enter the PCM rating"
                />
              )}
            />
            {errors.pcm && (
              <div className="text-danger mt-1">{errors.pcm.message}</div>
            )}
          </div>
          <div>
            <Label>
              Performance
              <span className="text-danger"> *</span>
            </Label>
            <Controller
              name="performance"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  className="external-filter-input form-control"
                  type="text"
                  placeholder="Enter"
                  title="Enter the Performance rating"
                />
              )}
            />
            {errors.performance && (
              <div className="text-danger mt-1">
                {errors.performance.message}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="modalFooter">
          <Button
            className="item custom-light-btn"
            onClick={toggle}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="item custom-btn"
            type="submit"
            disabled={!isValid || isPending}
          >
            {isPending ? 'Updating...' : 'Update'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default EditRatingsModal;
